import { CheckOutlined, CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Space, Form, Input, Popover, Typography, Tag, Modal } from 'antd';
import React, { useEffect, useState } from 'react';
import { GridCellChangeRequest, GridRow } from '../../models';
import { apiClient, apiErrorMessage } from '../../utils';

export function ChangeRequestActionButtons(props: {
  valueTask: GridCellChangeRequest;
  hasActiveOperation?: boolean;
  canEvaluate?: boolean;
  onOperationStarted?: () => void;
  onOperationEnded?: () => void;
  onSaved: (row: GridRow) => void;
  includeTasks?: boolean;
  predictions?: { value: string; confidence: string }[];
}) {
  const { Text } = Typography;

  const [loading, setLoading] = useState(false);
  const [showValidationPopover, setShowValidationPopover] = useState(false);
  const [showRejectPopover, setShowRejectPopover] = useState(false);
  const [errors, setErrors] = useState<string[]>();
  const [confidence, setConfidence] = useState<number>();
  const [confidenceOrder, setConfidenceOrder] = useState<number>();

  useEffect(() => {
    if (props.predictions?.length && Array.isArray(props.predictions)) {
      const confidenceIndex = props.predictions.findIndex(p => p.value === props.valueTask.value);
      const confidenceString = props.predictions[confidenceIndex]?.confidence;
      if (confidenceString) {
        const confidenceNumber = Number(confidenceString.split('.')[1].substring(0, 2));
        setConfidence(confidenceNumber);
        setConfidenceOrder(confidenceIndex + 1);
      }
    } else {
      setConfidence(undefined);
      setConfidenceOrder(undefined);
    }
  }, [confidence, props.valueTask.value, props.predictions]);

  const approve = () => {
    setLoading(true);
    setErrors(undefined);
    if (props.onOperationStarted) {
      props.onOperationStarted();
    }

    let url = `records/change-requests/${props.valueTask.id}/approve`;
    if (props.includeTasks) {
      url += '?includeTasks=true';
    }
    apiClient
      .post(url, { predictions: props.predictions ? JSON.stringify(props.predictions) : undefined })
      .then((response: any) => {
        props.onSaved(response.data as GridRow);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
        if (reason?.response?.data?.errors?.length) {
          setErrors(reason?.response?.data?.errors as string[]);
          setShowValidationPopover(true);
        }
      })
      .finally(() => {
        setLoading(false);
        if (props.onOperationEnded) {
          props.onOperationEnded();
        }
      });
  };

  const onApproveClick = () => {
    if (confidenceOrder && confidenceOrder >= 1) {
      confirmApprove();
    } else {
      approve();
    }
  };

  const confirmApprove = () => {
    const onOk = () => approve();
    Modal.confirm({
      title: 'Onaylama',
      icon: <ExclamationCircleOutlined />,
      content: 'En yüksek güven oranına sahip olan değer dışında bir değeri onaylıyorsunuz. Devam etmek istediğinize emin misiniz?',
      onOk,
    });
  };

  const onValidationVisibleChange = (visible: boolean) => {
    if (!visible) {
      setShowValidationPopover(false);
    }
  };

  const onRejectVisibleChange = (visible: boolean) => {
    setShowRejectPopover(visible);
  };

  const onRejectCancel = () => {
    setShowRejectPopover(false);
  };

  return (
    <>
      <Space>
        <Popover
          trigger="click"
          placement="top"
          visible={showValidationPopover}
          onVisibleChange={onValidationVisibleChange}
          destroyTooltipOnHide={true}
          content={
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {errors?.map((ve, i) => (
                <li key={i}>
                  <Text type="danger">{ve}</Text>
                </li>
              ))}
            </ul>
          }
        >
          <Space>
            {confidence && <ConfidenceTag confidence={confidence} />}
            <Button
              type="primary"
              shape="circle"
              size="small"
              icon={<CheckOutlined />}
              loading={loading}
              disabled={!props.canEvaluate || props.hasActiveOperation}
              onClick={onApproveClick}
            />
          </Space>
        </Popover>
        <Popover
          trigger="click"
          visible={showRejectPopover}
          onVisibleChange={onRejectVisibleChange}
          destroyTooltipOnHide={true}
          content={
            <RejectForm
              valueTask={props.valueTask}
              predictions={props.predictions}
              onSaved={props.onSaved}
              onCancel={onRejectCancel}
            />
          }
        >
          <Button
            type="default"
            shape="circle"
            size="small"
            icon={<CloseOutlined />}
            disabled={!props.canEvaluate || props.hasActiveOperation}
          />
        </Popover>
      </Space>
    </>
  );
}

function RejectForm(props: {
  valueTask: GridCellChangeRequest;
  predictions?: { value: string; confidence: string }[];
  onSaved: (row: GridRow) => void;
  onCancel: () => void;
}) {
  const [loading, setLoading] = useState(false);

  const onFinish = (values: any) => {
    setLoading(true);
    apiClient
      .post(`records/change-requests/${props.valueTask.id}/reject`, {
        ...values,
        predictions: props.predictions ? JSON.stringify(props.predictions) : undefined,
      })
      .then((response: any) => {
        props.onSaved(response.data as GridRow);
        props.onCancel();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Form layout="vertical" name="basic" onFinish={onFinish}>
      <Form.Item label="Açıklama" name="actionMessage" rules={[{ max: 4000, message: 'En fazla 4000 karakter' }]}>
        <Input.TextArea rows={3} />
      </Form.Item>
      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            Gönder
          </Button>
          <Button type="default" htmlType="button" onClick={props.onCancel}>
            İptal
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}

function ConfidenceTag(props: { confidence: number }) {
  const color = props.confidence > 90 ? 'success' : props.confidence > 80 ? 'warning' : 'default';
  return <Tag color={color}>%{props.confidence}</Tag>;
}
